import React from 'react';
import useSWR from 'swr';
import { CgSpinnerAlt } from 'react-icons/cg';
import config from '../lib/config';
import { fetcher } from '@/utils/fetcher';
import { IHeadToHeadResponse } from '../models/head-to-head';
import HeadToHeadSummary from './head-to-head-summary';
import HeadToHeadMatchList from './head-to-head-match-list';
import { H4, Muted } from './text';

interface IProps {
	homeContestantId: string;
	awayContestantId: string;
	competitionId?: string;
	homeTeamName: string;
	awayTeamName: string;
}

/**
 * HeadToHeadTab Component
 *
 * Container component for the head-to-head feature.
 * Fetches historical match data and displays summary statistics
 * and the last 5 match results.
 *
 * If competitionId is not provided, the backend will use the active
 * competition from WordPress settings.
 */
export default function HeadToHeadTab({
	homeContestantId,
	awayContestantId,
	competitionId,
	homeTeamName,
	awayTeamName,
}: IProps) {
	// Construct API URL with query parameters
	const competitionParam = competitionId ? `&competition=${competitionId}` : '';
	const url = `${config.apiUrl}/wp-json/statsperform/v1/head-to-head?home=${homeContestantId}&away=${awayContestantId}${competitionParam}`;

	// Fetch data using SWR
	const { data, error, isLoading } = useSWR<IHeadToHeadResponse>(url, fetcher, {
		revalidateOnFocus: false, // Historical data doesn't change frequently
		dedupingInterval: 60000, // Cache for 1 minute
		refreshInterval: 0, // Historical data is static within a session; 0 = disabled in SWR 2.x
	});

	// Loading state
	if (isLoading) {
		return (
			<div className="flex flex-col justify-center items-center py-16">
				<CgSpinnerAlt className="animate-spin text-black w-12 h-12 mb-4" />
				<p className="text-gray-500 text-sm">Onderlinge resultaten worden geladen...</p>
			</div>
		);
	}

	// Error state
	if (error) {
		return (
			<div className="text-center py-16">
				<div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
					<svg
						className="w-8 h-8 text-red-600"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
						/>
					</svg>
				</div>
				<p className="text-red-600 font-medium mb-2">Kon onderlinge resultaten niet laden</p>
				<p className="text-gray-500 text-sm">Probeer het later opnieuw.</p>
			</div>
		);
	}

	// No data or incomplete data state
	if (!data) {
		return null;
	}

	// Handle missing summary or matches (API returned but incomplete)
	if (!data.summary || !Array.isArray(data.matches)) {
		console.warn('Incomplete head-to-head data:', data);
		return (
			<div className="text-center py-8 text-gray-500">
				<p>Kon onderlinge resultaten niet correct laden</p>
			</div>
		);
	}

	return (
		<div className="py-4 ">
			{/* Summary Statistics */}
			{data.summary && (
				<HeadToHeadSummary summary={data.summary} homeTeamName={homeTeamName} awayTeamName={awayTeamName} />
			)}

			{/* Match List Header */}
			<div className="mb-4 lg:mt-12">
				<H4 className="">Laatste 5 wedstrijden</H4>
				{data.competitionName && <Muted className=" mt-1">In de {data.competitionName}</Muted>}
			</div>

			{/* Historical Matches */}
			<HeadToHeadMatchList matches={data.matches} />

			{/* Additional Info */}
			{data.matches && data.matches.length > 5 && (
				<div className="mt-4 text-center text-sm text-gray-500">
					Toont de 5 meest recente wedstrijden van totaal {data.summary.totalMatches} ontmoetingen
				</div>
			)}
		</div>
	);
}
